import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { CareShell, Panel } from "../../components/care/CareLayout";
import { useAuth } from "../../contexts/AuthContext";
import { careService } from "../../services/careService";

const number = (value) => Number(value || 0).toLocaleString();

const formatDate = (value, options = {}) => {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: options.year === false ? undefined : "numeric",
  });
};

const formatTime = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
};

const priorityClass = (priority = "") => String(priority || "Low").toLowerCase();

const statusLabel = (value = "") =>
  String(value || "")
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const metricCards = (metrics = {}) => [
  {
    icon: "ti-users-group",
    label: "Active residents",
    value: metrics.activeResidents,
    note: `${number(metrics.totalResidents)} total residents`,
    tone: "green",
  },
  {
    icon: "ti-clipboard-check",
    label: "Assessments active",
    value: metrics.activeAssessments,
    note: `${number(metrics.completedAssessments)} completed`,
    tone: "blue",
  },
  {
    icon: "ti-calendar-due",
    label: "Care plans due",
    value: metrics.ncpDueSoon,
    note: "Due within 14 days",
    tone: "amber",
  },
  {
    icon: "ti-clock-hour-4",
    label: "Staff on duty",
    value: metrics.staffOnDuty,
    note: `${number(metrics.upcomingSchedules)} shifts this week`,
    tone: "purple",
  },
];

function EmptyState({ children }) {
  return <div className="care-dashboard-empty">{children}</div>;
}

function MetricCard({ metric }) {
  return (
    <article className={`care-dashboard-metric is-${metric.tone}`}>
      <span className="care-dashboard-metric__icon">
        <i className={`ti ${metric.icon}`} />
      </span>
      <span>
        <small>{metric.label}</small>
        <strong>{number(metric.value)}</strong>
        <em>{metric.note}</em>
      </span>
    </article>
  );
}

function ResidentAttentionList({ residents }) {
  if (!residents.length) {
    return <EmptyState>No resident alerts or reviews need attention right now.</EmptyState>;
  }

  return (
    <div className="care-dashboard-list">
      {residents.map((resident) => (
        <Link className="care-dashboard-row" to={`/residents/${resident.id}`} key={resident.id}>
          <span className="care-dashboard-avatar">{resident.initials || "--"}</span>
          <span className="care-dashboard-row__main">
            <strong>{resident.name || "Unnamed resident"}</strong>
            <small>{resident.room ? `Room ${resident.room}` : statusLabel(resident.status)}</small>
          </span>
          <span className="care-dashboard-row__detail">
            <b>{resident.ncpStatusLabel || resident.currentIssue || "Review"}</b>
            <small>{resident.nextReview ? `Next review ${formatDate(resident.nextReview)}` : resident.currentIssue}</small>
          </span>
          <em className={`pill ${priorityClass(resident.priority)}`}>{resident.priority || "Low"}</em>
        </Link>
      ))}
    </div>
  );
}

function CarePlanList({ carePlans }) {
  if (!carePlans.length) {
    return <EmptyState>No care plans are due in the next 14 days.</EmptyState>;
  }

  return (
    <div className="care-dashboard-list">
      {carePlans.map((plan) => (
        <Link className="care-dashboard-row" to={plan.residentId ? `/residents/${plan.residentId}#care-plans` : "/care-plans/negotiated"} key={plan.id}>
          <span className="care-dashboard-avatar blue">{plan.initials || "--"}</span>
          <span className="care-dashboard-row__main">
            <strong>{plan.residentName || "Unnamed resident"}</strong>
            <small>{plan.room ? `Room ${plan.room}` : plan.title || "Negotiated care plan"}</small>
          </span>
          <span className="care-dashboard-row__detail">
            <b>{statusLabel(plan.status)}</b>
            <small>{formatDate(plan.dueDate)}</small>
          </span>
          <em className={`pill ${plan.dueStatus === "Overdue" ? "high" : plan.dueStatus === "Due today" ? "medium" : "blue"}`}>
            {plan.dueStatus || "Upcoming"}
          </em>
        </Link>
      ))}
    </div>
  );
}

function ScheduleList({ schedules }) {
  if (!schedules.length) {
    return <EmptyState>No shifts are scheduled for the next 7 days.</EmptyState>;
  }

  return (
    <div className="care-dashboard-schedule-list">
      {schedules.map((schedule) => (
        <Link className="care-dashboard-schedule" to="/schedules" key={schedule.id}>
          <span className="care-dashboard-schedule__date">
            <strong>{formatDate(schedule.startTimestamp, { year: false })}</strong>
            <small>{formatTime(schedule.startTimestamp)} - {formatTime(schedule.endTimestamp)}</small>
          </span>
          <span>
            <b>{schedule.caregiverName || "Care staff"}</b>
            <small>{statusLabel(schedule.scheduleType)} • {statusLabel(schedule.status)}</small>
          </span>
          <i className="ti ti-chevron-right" />
        </Link>
      ))}
    </div>
  );
}

function AssessmentStatusPanel({ assessmentStatuses = {} }) {
  const statuses = Object.entries(assessmentStatuses || {})
    .map(([status, total]) => ({ status, total: Number(total || 0) }))
    .filter((item) => item.total > 0)
    .sort((a, b) => b.total - a.total);
  const total = statuses.reduce((sum, item) => sum + item.total, 0);
  const completed = Number(assessmentStatuses.COMPLETED || 0);
  const completedPct = total ? Math.round((completed / total) * 100) : 0;

  return (
    <div className="care-dashboard-assessments">
      <div className="care-dashboard-donut" style={{ "--pct": `${completedPct}%` }}>
        <strong>{completedPct}%</strong>
        <span>completed</span>
      </div>
      <div className="care-dashboard-statuses">
        {statuses.length ? statuses.slice(0, 5).map((item) => (
          <span key={item.status}>
            <b>{statusLabel(item.status)}</b>
            <em>{number(item.total)}</em>
          </span>
        )) : <EmptyState>No assessment records yet.</EmptyState>}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const actorId = user?.id || user?.userId || user?.uid || "";
  const [dashboard, setDashboard] = useState({
    metrics: {},
    residents: [],
    carePlans: [],
    schedules: [],
    assessmentStatuses: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    careService.dashboard({}, actorId)
      .then((data) => {
        if (!active) return;
        setDashboard({
          metrics: data?.metrics || {},
          residents: Array.isArray(data?.residents) ? data.residents : [],
          carePlans: Array.isArray(data?.carePlans) ? data.carePlans : [],
          schedules: Array.isArray(data?.schedules) ? data.schedules : [],
          assessmentStatuses: data?.assessmentStatuses || {},
        });
        setError("");
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.response?.data?.message || err?.message || "Unable to load care dashboard.");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [actorId]);

  const metrics = useMemo(() => metricCards(dashboard.metrics), [dashboard.metrics]);

  return (
    <CareShell className="dashboard-content care-dashboard-page">
      <div className="care-dashboard-hero">
        <div>
          <span>Workspace overview</span>
          <h1>Care Dashboard</h1>
          <p>Live resident, care plan, assessment, and staffing signals for this workspace.</p>
        </div>
        <Link className="button-primary" to="/residents">
          <i className="ti ti-users" /> Residents
        </Link>
      </div>

      {error ? <p className="form-error">{error}</p> : null}

      <div className="care-dashboard-metrics">
        {metrics.map((metric) => (
          <MetricCard metric={metric} key={metric.label} />
        ))}
      </div>

      <div className="care-dashboard-grid">
        <Panel title="Residents Requiring Attention">
          {loading ? <EmptyState>Loading residents...</EmptyState> : <ResidentAttentionList residents={dashboard.residents} />}
          <Link className="text-action align-end" to="/residents">Open residents <i className="ti ti-arrow-right" /></Link>
        </Panel>

        <Panel title="Care Plans Due Soon">
          {loading ? <EmptyState>Loading care plans...</EmptyState> : <CarePlanList carePlans={dashboard.carePlans} />}
          <Link className="text-action align-end" to="/care-plans/negotiated">Open care plans <i className="ti ti-arrow-right" /></Link>
        </Panel>

        <Panel title="Upcoming Staff Schedule">
          {loading ? <EmptyState>Loading schedule...</EmptyState> : <ScheduleList schedules={dashboard.schedules} />}
          <Link className="text-action align-end" to="/schedules">Open schedules <i className="ti ti-arrow-right" /></Link>
        </Panel>

        <Panel title="Assessment Status">
          {loading ? <EmptyState>Loading assessments...</EmptyState> : <AssessmentStatusPanel assessmentStatuses={dashboard.assessmentStatuses} />}
          <Link className="text-action align-end" to="/assessments">Open assessments <i className="ti ti-arrow-right" /></Link>
        </Panel>
      </div>
    </CareShell>
  );
}
